import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SystemAdminService, AuditLogEntry } from '../../../services/system-admin/system-admin.service';

@Component({
  selector: 'app-admin-audit-log',
  imports: [CommonModule, FormsModule],
  templateUrl: './audit-log.html',
})
export class AdminAuditLog implements OnInit {
  private systemAdminService = inject(SystemAdminService);
  private destroyRef = inject(DestroyRef);

  entries = signal<AuditLogEntry[]>([]);
  loading = signal(true);
  page = 1;
  pageSize = 50;
  totalCount = 0;
  actionFilter = 'all';

  ngOnInit(): void {
    this.loadEntries();
  }

  loadEntries(): void {
    this.loading.set(true);
    const actionType = this.actionFilter === 'all' ? undefined : this.actionFilter;
    this.systemAdminService.getAuditLog({
      page: this.page,
      pageSize: this.pageSize,
      actionType,
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.entries.set(response.entries);
        this.totalCount = response.totalCount;
        this.loading.set(false);
      },
      error: () => this.loading.set(false),
    });
  }

  onFilterChange(): void {
    this.page = 1;
    this.loadEntries();
  }

  onLoadMore(): void {
    this.page++;
    this.systemAdminService.getAuditLog({
      page: this.page,
      pageSize: this.pageSize,
      actionType: this.actionFilter === 'all' ? undefined : this.actionFilter,
    }).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (response) => {
        this.entries.update(current => [...current, ...response.entries]);
        this.totalCount = response.totalCount;
      }
    });
  }

  formatDate(date: string): string {
    if (!date) return '';
    return formatDate(date, 'dd/MM/yyyy HH:mm', 'es-CO');
  }

  getActionIcon(action: string): string {
    const map: Record<string, string> = {
      'impersonate': 'user',
      'activate': 'play',
      'deactivate': 'pause',
      'extend_membership': 'calendar',
      'set_membership_date': 'calendar',
      'reset_business': 'refresh',
      'delete_business': 'trash',
      'support_reply': 'info',
      'support_status': 'info',
    };
    return map[action] || 'info';
  }

  getActionColor(action: string): string {
    const map: Record<string, string> = {
      'impersonate': 'text-blue-500 bg-blue-50 dark:bg-blue-500/10',
      'activate': 'text-success-500 bg-success-50 dark:bg-success-500/10',
      'deactivate': 'text-warning-500 bg-warning-50 dark:bg-warning-500/10',
      'extend_membership': 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      'set_membership_date': 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      'reset_business': 'text-error-500 bg-error-50 dark:bg-error-500/10',
      'delete_business': 'text-error-500 bg-error-50 dark:bg-error-500/10',
      'support_reply': 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      'support_status': 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
    };
    return map[action] || 'text-gray-500 bg-gray-50 dark:bg-gray-800';
  }

  getActionLabel(action: string): string {
    const map: Record<string, string> = {
      'impersonate': 'Impersonación',
      'activate': 'Activación',
      'deactivate': 'Desactivación',
      'extend_membership': 'Extensión de membresía',
      'set_membership_date': 'Cambio fecha membresía',
      'reset_business': 'Reinicio de negocio',
      'delete_business': 'Eliminación de negocio',
      'support_reply': 'Respuesta de soporte',
      'support_status': 'Estado de ticket de soporte',
    };
    return map[action] || action;
  }
}
